import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import type { SubmissionState } from "@/types/submissions"

/**
 * OKS-35 — Per-submission state pill.
 *
 *   pending  → blue    (info)
 *   approved → emerald (success)
 *   flagged  → amber   (warning)
 *
 * Mirrors `<CollabStatePill>` posture (color + text always paired —
 * accessibility lock from UX Spec §9). The label is translated from
 * `submissions.state.<state>`.
 *
 * OKS-562 — the `approved` chip plays the one-shot `content-approved` beat
 * (opacity + scale settle, 200ms ease-out) as it mounts, so the moment a
 * submission reaches approved reads as a crisp confirmation. Single-entrance
 * + `motion-safe:` gated (static under reduced-motion). Bounded to the
 * `approved` tone only — pending/flagged chips do not animate.
 */

type Tone = "info" | "success" | "warning"

const TONE_TO_CLASSES: Record<Tone, string> = {
  info: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100 border-transparent",
  success:
    "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-100 border-transparent",
  warning:
    "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100 border-transparent",
}

const STATE_TO_TONE: Record<SubmissionState, Tone> = {
  pending: "info",
  approved: "success",
  flagged: "warning",
}

interface Props {
  state: SubmissionState
  className?: string
}

export function SubmissionStateChip({ state, className }: Props) {
  const { t } = useTranslation()
  const tone = STATE_TO_TONE[state]

  return (
    <Badge
      variant="secondary"
      data-testid={`submission-state-chip-${state}`}
      className={cn(
        "rounded-full px-2.5 py-0.5 text-xs",
        TONE_TO_CLASSES[tone],
        state === "approved" && MOTION_BEATS["content-approved"],
        className,
      )}
    >
      {t(`submissions.state.${state}`)}
    </Badge>
  )
}
